import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Tooltip,
  LinearProgress,
  Tabs,
  Tab,
  Badge,
  Alert,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  Person as PersonIcon,
  Work as WorkIcon,
  Business as BusinessIcon,
  Schedule as ScheduleIcon,
} from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import axios from "axios";
import { workerDetailsAPI } from "../services/api";
import { useSnackbar } from "notistack";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { contractorDetailsAPI } from "../services/api";
import {
  calculateProfileCompletion,
  canContractorAddWorkers,
} from "../utils/profileCompletion";
import WorkerForm from "../components/Workers/WorkerForm";

const Workers = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openForm, setOpenForm] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterContractor, setFilterContractor] = useState("");
  const [contractors, setContractors] = useState([]);
  const [profileData, setProfileData] = useState(null);
  const [profileCompletion, setProfileCompletion] = useState({
    completionPercentage: 0,
    isComplete: false,
  });

  const statusOptions = [
    { value: "all", label: "All Status" },
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
    { value: "terminated", label: "Terminated" },
    { value: "on_leave", label: "On Leave" },
  ];

  const tabs = [
    { label: "All Workers", value: 0 },
    { label: "Active", value: 1 },
    { label: "Medical Pending", value: 2 },
    { label: "Document Expired", value: 3 },
  ];

  const loadWorkers = useCallback(async () => {
    try {
      setLoading(true);
      // Map current UI filters to API query params
      const params = {
        page: 1,
        limit: 50,
        search: searchTerm || undefined,
        contractorId: filterContractor || undefined,
        // Tab-based params
        medicalStatus: tabValue === 2 ? "pending" : undefined,
        validityStatus: tabValue === 3 ? "expired" : undefined,
      };

      const response = await workerDetailsAPI.getAll(params);
      setWorkers(response.data.workers || []);
    } catch (error) {
      enqueueSnackbar("Failed to load workers", { variant: "error" });
      console.error("Error loading workers:", error);
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  const loadContractors = useCallback(async () => {
    try {
      const response = await axios.get("/api/contractor-details", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "x-tenant-id": localStorage.getItem("tenantId"),
        },
      });
      setContractors(response.data.contractors || []);
    } catch (error) {
      console.error("Error loading contractors:", error);
    }
  }, []);

  const loadContractorProfile = useCallback(async () => {
    if (
      !["contractor_user", "contractor", "contractor_admin"].includes(
        user?.role
      )
    ) {
      return;
    }

    try {
      // Try to get profile status first
      const response = await contractorDetailsAPI.getProfileStatus();
      if (response.data.success) {
        const profileData = response.data.data;
        setProfileData(profileData);
        const completion = calculateProfileCompletion(profileData);
        setProfileCompletion(completion);
      } else {
        setProfileData(null);
        setProfileCompletion({ completionPercentage: 0, isComplete: false });
      }
    } catch (error) {
      console.error("Error loading contractor profile:", error);
      setProfileData(null);
      setProfileCompletion({ completionPercentage: 0, isComplete: false });
    }
  }, [user?.role]);

  useEffect(() => {
    loadWorkers();
    loadContractors();
    loadContractorProfile();
  }, [loadWorkers, loadContractors, loadContractorProfile]);

  const handleCreateWorker = () => {
    // Check if contractor user has completed profile
    if (
      ["contractor_user", "contractor", "contractor_admin"].includes(user?.role)
    ) {
      const { canAddWorkers, reason } = canContractorAddWorkers(profileData);
      if (!canAddWorkers) {
        enqueueSnackbar(reason, { variant: "warning" });
        return;
      }
    }

    setSelectedWorker(null);
    setOpenForm(true);
  };

  const handleEditWorker = (worker) => {
    setSelectedWorker(worker);
    setOpenForm(true);
  };

  const handleViewWorker = (worker) => {
    setSelectedWorker(worker);
    setOpenViewDialog(true);
  };

  const handleDeleteWorker = async (workerId) => {
    if (window.confirm("Are you sure you want to delete this worker?")) {
      try {
        await axios.delete(`/api/worker-details/${workerId}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "x-tenant-id": localStorage.getItem("tenantId"),
          },
        });
        enqueueSnackbar("Worker deleted successfully", { variant: "success" });
        loadWorkers();
      } catch (error) {
        enqueueSnackbar("Failed to delete worker", { variant: "error" });
      }
    }
  };

  const handleFormClose = () => {
    setOpenForm(false);
    setSelectedWorker(null);
  };

  const handleFormSave = () => {
    setOpenForm(false);
    setSelectedWorker(null);
    loadWorkers();
    enqueueSnackbar("Worker saved successfully", { variant: "success" });
  };

  const getFilteredWorkers = () => {
    let filtered = workers;

    // Tab filtering
    switch (tabValue) {
      case 1: // Active
        filtered = filtered.filter(
          (w) => w.employmentDetails?.status === "active"
        );
        break;
      case 2: // Medical Pending
        filtered = filtered.filter(
          (w) =>
            w.medicalDetails?.medicalStatus === "pending" ||
            w.medicalDetails?.medicalStatus === "expired"
        );
        break;
      case 3: // Document Expired
        filtered = filtered.filter((w) => {
          const validity = w.identificationDetails?.validityDetails;
          if (!validity) return false;
          const now = new Date();
          return (
            (validity.pfValidTill && new Date(validity.pfValidTill) < now) ||
            (validity.esicValidTill &&
              new Date(validity.esicValidTill) < now) ||
            (validity.medicalValidTill &&
              new Date(validity.medicalValidTill) < now)
          );
        });
        break;
      default:
        break;
    }

    // Search filtering
    if (searchTerm) {
      filtered = filtered.filter(
        (w) =>
          w.personalDetails?.firstName
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          w.personalDetails?.lastName
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          w.identificationDetails?.pfNumber
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          w.identificationDetails?.aadhaarNumber?.includes(searchTerm)
      );
    }

    // Status filtering
    if (filterStatus !== "all") {
      filtered = filtered.filter(
        (w) => w.employmentDetails?.status === filterStatus
      );
    }

    // Contractor filtering
    if (filterContractor) {
      filtered = filtered.filter(
        (w) => w.employmentDetails?.contractorId === filterContractor
      );
    }

    return filtered;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "success";
      case "inactive":
        return "warning";
      case "terminated":
        return "error";
      case "on_leave":
        return "info";
      default:
        return "default";
    }
  };

  const getMedicalStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "success";
      case "pending":
        return "warning";
      case "failed":
        return "error";
      case "expired":
        return "error";
      default:
        return "default";
    }
  };

  const renderWorkerDetails = () => {
    if (!selectedWorker) return null;

    return (
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Personal Details
              </Typography>
              <Typography variant="body2">
                <strong>Name:</strong>{" "}
                {selectedWorker.personalDetails?.firstName}{" "}
                {selectedWorker.personalDetails?.lastName}
              </Typography>
              <Typography variant="body2">
                <strong>DOB:</strong>{" "}
                {selectedWorker.personalDetails?.dateOfBirth
                  ? dayjs(selectedWorker.personalDetails.dateOfBirth).format(
                      "DD/MM/YYYY"
                    )
                  : "N/A"}
              </Typography>
              <Typography variant="body2">
                <strong>Gender:</strong>{" "}
                {selectedWorker.personalDetails?.gender || "N/A"}
              </Typography>
              <Typography variant="body2">
                <strong>Blood Group:</strong>{" "}
                {selectedWorker.personalDetails?.bloodGroup || "N/A"}
              </Typography>
              <Typography variant="body2">
                <strong>Phone:</strong>{" "}
                {selectedWorker.personalDetails?.phoneNumber || "N/A"}
              </Typography>
              <Typography variant="body2">
                <strong>Email:</strong>{" "}
                {selectedWorker.personalDetails?.emailAddress || "N/A"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Identification Details
              </Typography>
              <Typography variant="body2">
                <strong>PF Number:</strong>{" "}
                {selectedWorker.identificationDetails?.pfNumber || "N/A"}
              </Typography>
              <Typography variant="body2">
                <strong>ESIC Number:</strong>{" "}
                {selectedWorker.identificationDetails?.esicNumber || "N/A"}
              </Typography>
              <Typography variant="body2">
                <strong>PAN Number:</strong>{" "}
                {selectedWorker.identificationDetails?.panNumber || "N/A"}
              </Typography>
              <Typography variant="body2">
                <strong>Aadhaar Number:</strong>{" "}
                {selectedWorker.identificationDetails?.aadhaarNumber || "N/A"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Employment Details
              </Typography>
              <Typography variant="body2">
                <strong>Designation:</strong>{" "}
                {selectedWorker.employmentDetails?.designation || "N/A"}
              </Typography>
              <Typography variant="body2">
                <strong>Department:</strong>{" "}
                {selectedWorker.employmentDetails?.department || "N/A"}
              </Typography>
              <Typography variant="body2">
                <strong>Joining Date:</strong>{" "}
                {selectedWorker.employmentDetails?.joiningDate
                  ? dayjs(selectedWorker.employmentDetails.joiningDate).format(
                      "DD/MM/YYYY"
                    )
                  : "N/A"}
              </Typography>
              <Typography variant="body2">
                <strong>Status:</strong>{" "}
                {selectedWorker.employmentDetails?.status || "N/A"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Medical Details
              </Typography>
              <Typography variant="body2">
                <strong>Pre-Medical Date:</strong>{" "}
                {selectedWorker.medicalDetails?.preMedicalDate
                  ? dayjs(selectedWorker.medicalDetails.preMedicalDate).format(
                      "DD/MM/YYYY"
                    )
                  : "N/A"}
              </Typography>
              <Typography variant="body2">
                <strong>Medical Status:</strong>{" "}
                {selectedWorker.medicalDetails?.medicalStatus || "N/A"}
              </Typography>
              <Typography variant="body2">
                <strong>Allergies:</strong>{" "}
                {selectedWorker.medicalDetails?.allergies?.join(", ") || "None"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const filteredWorkers = getFilteredWorkers();

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="h4">Worker Management</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateWorker}
            disabled={
              ["contractor_user", "contractor", "contractor_admin"].includes(
                user?.role
              ) && !profileCompletion.isComplete
            }
          >
            Add Worker
          </Button>
        </Box>

        {/* Profile Progress Alert for Contractors */}
        {["contractor_user", "contractor", "contractor_admin"].includes(
          user?.role
        ) &&
          !profileCompletion.isComplete && (
            <Alert
              severity="warning"
              sx={{ mb: 3 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => navigate("/profile")}
                >
                  Complete Profile
                </Button>
              }
            >
              <Typography variant="body2">
                <strong>Profile completion required to add workers.</strong>{" "}
                Complete your profile ({profileCompletion.completionPercentage}
                %) to access all features.
              </Typography>
            </Alert>
          )}

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <PersonIcon color="primary" sx={{ mr: 2 }} />
                  <Box>
                    <Typography variant="h4">{workers.length}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Workers
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <WorkIcon color="success" sx={{ mr: 2 }} />
                  <Box>
                    <Typography variant="h4">
                      {
                        workers.filter(
                          (w) => w.employmentDetails?.status === "active"
                        ).length
                      }
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Active Workers
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <ScheduleIcon color="warning" sx={{ mr: 2 }} />
                  <Box>
                    <Typography variant="h4">
                      {
                        workers.filter(
                          (w) => w.medicalDetails?.medicalStatus === "pending"
                        ).length
                      }
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Medical Pending
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <BusinessIcon color="error" sx={{ mr: 2 }} />
                  <Box>
                    <Typography variant="h4">{contractors.length}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Contractors
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filters and Search */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  placeholder="Search workers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <SearchIcon color="action" sx={{ mr: 1 }} />
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    label="Status"
                  >
                    {statusOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth>
                  <InputLabel>Contractor</InputLabel>
                  <Select
                    value={filterContractor}
                    onChange={(e) => setFilterContractor(e.target.value)}
                    label="Contractor"
                  >
                    <MenuItem value="">All Contractors</MenuItem>
                    {contractors.map((contractor) => (
                      <MenuItem key={contractor._id} value={contractor._id}>
                        {contractor.companyName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button variant="outlined" startIcon={<FilterIcon />}>
                    More Filters
                  </Button>
                  <Button variant="outlined" startIcon={<DownloadIcon />}>
                    Export
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Card sx={{ mb: 3 }}>
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
          >
            {tabs.map((tab) => (
              <Tab
                key={tab.value}
                label={
                  <Badge
                    badgeContent={
                      tab.value === 0
                        ? workers.length
                        : tab.value === 1
                        ? workers.filter(
                            (w) => w.employmentDetails?.status === "active"
                          ).length
                        : tab.value === 2
                        ? workers.filter(
                            (w) => w.medicalDetails?.medicalStatus === "pending"
                          ).length
                        : workers.filter((w) => {
                            const validity =
                              w.identificationDetails?.validityDetails;
                            if (!validity) return false;
                            const now = new Date();
                            return (
                              (validity.pfValidTill &&
                                new Date(validity.pfValidTill) < now) ||
                              (validity.esicValidTill &&
                                new Date(validity.esicValidTill) < now) ||
                              (validity.medicalValidTill &&
                                new Date(validity.medicalValidTill) < now)
                            );
                          }).length
                    }
                    color="primary"
                  >
                    {tab.label}
                  </Badge>
                }
              />
            ))}
          </Tabs>
        </Card>

        {/* Workers Table */}
        <Card>
          <CardContent>
            {loading && <LinearProgress sx={{ mb: 2 }} />}

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Worker</TableCell>
                    <TableCell>PF Number</TableCell>
                    <TableCell>Contractor</TableCell>
                    <TableCell>Designation</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Medical Status</TableCell>
                    <TableCell>Joining Date</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredWorkers.map((worker) => (
                    <TableRow key={worker._id}>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Avatar sx={{ mr: 2, bgcolor: "primary.main" }}>
                            {worker.personalDetails?.firstName?.[0]?.toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2">
                              {worker.personalDetails?.firstName}{" "}
                              {worker.personalDetails?.lastName}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {worker.personalDetails?.phoneNumber}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: "monospace" }}
                        >
                          {worker.identificationDetails?.pfNumber || "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {worker.contractorId?.companyName || "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {worker.employmentDetails?.designation || "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={worker.employmentDetails?.status || "N/A"}
                          color={getStatusColor(
                            worker.employmentDetails?.status
                          )}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={worker.medicalDetails?.medicalStatus || "N/A"}
                          color={getMedicalStatusColor(
                            worker.medicalDetails?.medicalStatus
                          )}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {worker.employmentDetails?.joiningDate
                            ? dayjs(
                                worker.employmentDetails.joiningDate
                              ).format("DD/MM/YYYY")
                            : "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleViewWorker(worker)}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit Worker">
                          <IconButton
                            size="small"
                            onClick={() => handleEditWorker(worker)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Worker">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteWorker(worker._id)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {filteredWorkers.length === 0 && (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography variant="h6" color="text.secondary">
                  No workers found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Try adjusting your search or filter criteria
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Worker Form Dialog */}
        <Dialog
          open={openForm}
          onClose={handleFormClose}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            {selectedWorker ? "Edit Worker" : "Add New Worker"}
          </DialogTitle>
          <DialogContent>
            <WorkerForm
              workerId={selectedWorker?._id}
              onSave={handleFormSave}
              onCancel={handleFormClose}
            />
          </DialogContent>
        </Dialog>

        {/* View Worker Dialog */}
        <Dialog
          open={openViewDialog}
          onClose={() => setOpenViewDialog(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>Worker Details</DialogTitle>
          <DialogContent>{renderWorkerDetails()}</DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenViewDialog(false)}>Close</Button>
            <Button
              variant="contained"
              onClick={() => {
                setOpenViewDialog(false);
                handleEditWorker(selectedWorker);
              }}
            >
              Edit Worker
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default Workers;
